import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { pluginApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Archive, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

export function BackupsPlugin({ serverId }: { serverId: string }) {
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const backupsPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'backups');
  const isEnabled = backupsPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) =>
      enabled ? pluginApi.enablePlugin(serverId, 'backups') : pluginApi.disablePlugin(serverId, 'backups'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'backups', config),
    {
      onSuccess: () => toast.success('Configuration saved!'),
      invalidateQueries: [['servers', serverId, 'plugins']]
    }
  );

  const [config, setConfig] = useState<any>({
    enabled: true,
    autoBackup: true,
    backupIntervalHours: 24,
    retentionCount: 10,
    includeRoles: true,
    includeChannels: true,
    includeEmojis: false
  });

  useEffect(() => {
    if (backupsPlugin?.config) {
      setConfig({ ...config, ...backupsPlugin.config });
    }
  }, [backupsPlugin]);

  if (!backupsPlugin) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Archive className="w-10 h-10 mx-auto mb-3 opacity-30" />
        <p>Plugin non disponibile per questo server.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-slate-500/20 rounded-xl">
            <Archive className="w-6 h-6 text-slate-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Server Backups</h3>
            <p className="text-sm text-muted-foreground">Backup e ripristino di canali e ruoli.</p>
          </div>
        </div>
        <Switch checked={isEnabled} onCheckedChange={(enabled) => togglePlugin({ enabled })} />
      </div>

      {isEnabled && (
        <>
          <div className="glass-card p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Intervallo backup (ore)</Label>
                <Input
                  type="number"
                  min="1"
                  max="168"
                  value={config.backupIntervalHours}
                  onChange={(e) => setConfig({ ...config, backupIntervalHours: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label>Backup conservati</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={config.retentionCount}
                  onChange={(e) => setConfig({ ...config, retentionCount: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
              <div>
                <Label>Auto backup</Label>
                <p className="text-xs text-muted-foreground">Esegui backup automaticamente</p>
              </div>
              <Switch checked={config.autoBackup} onCheckedChange={(checked) => setConfig({ ...config, autoBackup: checked })} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                <div>
                  <Label>Ruoli</Label>
                  <p className="text-xs text-muted-foreground">Includi ruoli</p>
                </div>
                <Switch checked={config.includeRoles} onCheckedChange={(checked) => setConfig({ ...config, includeRoles: checked })} />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                <div>
                  <Label>Canali</Label>
                  <p className="text-xs text-muted-foreground">Includi canali</p>
                </div>
                <Switch checked={config.includeChannels} onCheckedChange={(checked) => setConfig({ ...config, includeChannels: checked })} />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                <div>
                  <Label>Emoji</Label>
                  <p className="text-xs text-muted-foreground">Includi emoji</p>
                </div>
                <Switch checked={config.includeEmojis} onCheckedChange={(checked) => setConfig({ ...config, includeEmojis: checked })} />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => updateConfig(config)} disabled={saving} className="min-w-[160px]">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
